import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Alert, Button, Icon, InputText, TextArea } from "@/deps/ui-library";
import { useEmailDraft, useSendEmail, useSession } from "@/api/queries";
import { useToast } from "@/app/toasts";
import type { EmailTarget, EmailToSend } from "@/api/types";
import { useCan } from "@/app/session";
import { Dialog } from "@/components/Dialog";

const ADDRESS = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PLACEHOLDER = /\{\{\s*\w+\s*\}\}/;

const addresses = (text: string) =>
  text
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter(Boolean);

/** Why this can't go yet, or null. The API refuses the same things. */
function problemWith(to: string, cc: string, subject: string, body: string): string | null {
  const recipients = addresses(to);
  if (recipients.length === 0 || !recipients.every((a) => ADDRESS.test(a))) {
    return "Enter a valid recipient email address.";
  }
  if (!addresses(cc).every((a) => ADDRESS.test(a)))
    return "Enter valid email addresses to copy in.";
  if (subject.trim() === "") return "The subject is empty.";
  if (body.trim() === "") return "The message is empty.";
  if (PLACEHOLDER.test(subject) || PLACEHOLDER.test(body)) {
    return "Replace the unfilled placeholders before sending.";
  }
  return null;
}

/**
 * Sends a document by email. It starts from the company's template with the facts filled in, and a
 * change here applies to this email only.
 */
export function SendEmailDialog({
  target,
  onClose,
  onSent,
}: {
  target: EmailTarget;
  onClose: () => void;
  onSent: (recipients: string[]) => void;
}) {
  const { data: draft, error } = useEmailDraft(target);
  const can = useCan();
  const send = useSendEmail();
  const toast = useToast();
  const demo = useSession().data?.organization.sampleData === true;
  // One dialog from first to last, so it and its Cancel stay put when the draft arrives. What's
  // shown is the draft, until the person has changed it.
  const [edits, setEdits] = useState<Partial<EmailToSend>>({});
  const [refusal, setRefusal] = useState<string | null>(null);

  const to = edits.to ?? draft?.to ?? "";
  const cc = edits.cc ?? "";
  const subject = edits.subject ?? draft?.subject ?? "";
  const body = edits.body ?? draft?.body ?? "";

  const unfilled = PLACEHOLDER.test(subject) || PLACEHOLDER.test(body);
  const shown = refusal ?? (send.error as Error | null)?.message ?? null;

  const submit = () => {
    const problem = problemWith(to, cc, subject, body);
    setRefusal(problem);
    if (problem) return;
    send.mutate(
      { target, email: { to, cc, subject, body } },
      {
        onSuccess: (recipients) => {
          // The demo company's mail never leaves it (ADR-0063), so it isn't "on its way" to anyone.
          if (!demo) {
            onSent(recipients);
            return;
          }
          toast({
            title: "Simulated: the demo company sends nothing.",
            description: `In your own company this would go to ${recipients.join(", ")}.`,
            scheme: "info",
          });
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={draft?.title ?? "Send email"}
      description={draft ? "Changes here apply to this email only." : undefined}
      footer={
        draft ? (
          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            disabled={send.isPending}
            onClick={submit}
          >
            {send.isPending ? "Sending" : "Send"}
          </Button>
        ) : undefined
      }
    >
      {!draft ? (
        error ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(error as Error).message}
          />
        ) : (
          <p className="text-fg-muted text-sm">Loading…</p>
        )
      ) : (
        <div className="flex flex-col gap-3">
          {demo ? (
            <Alert
              config={{ variant: "subtle", colorScheme: "info" }}
              description="This is the demo company. Sending is simulated: nothing leaves it."
            />
          ) : null}
          {shown ? (
            <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={shown} />
          ) : null}
          <div className="grid gap-3 md:grid-cols-2">
            <InputText
              label="To"
              required
              placeholder="name@company.com"
              value={to}
              onChange={(event) => setEdits({ ...edits, to: event.target.value })}
            />
            <InputText
              label="Cc"
              placeholder="Optional"
              value={cc}
              onChange={(event) => setEdits({ ...edits, cc: event.target.value })}
            />
          </div>
          <InputText
            label="Subject"
            value={subject}
            onChange={(event) => setEdits({ ...edits, subject: event.target.value })}
          />
          <TextArea
            label="Message"
            rows={10}
            value={body}
            onChange={(event) => setEdits({ ...edits, body: event.target.value })}
          />
          {unfilled && !shown ? (
            <p className="text-fg-muted text-xs">
              Replace the unfilled placeholders before sending.
            </p>
          ) : null}
          <div className="border-border flex items-center gap-2.5 rounded-md border px-3 py-2">
            <Icon name="FileText" size="sm" className="text-fg-muted" />
            <div className="min-w-0">
              <p className="text-fg text-sm font-medium">{draft.attachment}</p>
              <p className="text-fg-muted text-xs">Attached as PDF</p>
            </div>
          </div>
          {can("settings.company.manage") ? (
            <Link to="/settings/email-templates" className="text-primary text-xs font-medium">
              Edit the template in Settings
            </Link>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
